import { KTag, KText } from '@/ui-library/components';
import {
  Flex,
  Box,
  Text,
  Circle,
  Spinner,
  Center,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
  memo,
} from 'react';
import { FixedSizeList as List } from 'react-window';
import {
  JobListProps,
  JobListOption,
  JobOption,
  JobListMenuProps,
} from './JobList.interface';
import { components } from 'react-select';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { KSwitch } from '@/ui-library/components/molecules/KSwitch';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { jobStatusConfig } from '@/pages/ats-job-creation/utils/constants';
import { requisitionStatusConfig } from '@/pages/requisitions/constants/constants';

const JOB_ITEM_BORDER = 1;
const getJobItemContentHeight = (showOnlyOpen: boolean) =>
  showOnlyOpen ? 65 : 71;
const getJobItemHeight = (showOnlyOpen: boolean) =>
  getJobItemContentHeight(showOnlyOpen) + JOB_ITEM_BORDER;
const VIRTUAL_LIST_THRESHOLD = 20;

// Format and render location
const renderJobLocation = (location: string | string[] | undefined) => {
  if (!location) return ' -';

  if (typeof location === 'string') return location;

  // Handle array of locations
  if (Array.isArray(location)) {
    if (location.length === 0) return ' -';
    if (location.length === 1)
      return (
        <KText isTruncated enableTooltip maxW="100%">
          {location[0]}
        </KText>
      );

    // Use ViewMorePopover for multiple locations
    return (
      <ViewMorePopover
        list={location}
        max={1}
        type="office"
        placement="bottom-start"
        trigger="hover"
      />
    );
  }

  return ' -';
};

// Job item component - memoized to avoid re-renders when parent updates
const JobItem = memo(function JobItem({
  job,
  onSelect,
  isSelected,
  menuWidth,
  showOnlyOpen,
  showBorder = true,
  contentMaxHeight,
}: {
  job: JobListOption;
  onSelect: (_option: JobListOption) => void;
  isSelected: boolean;
  menuWidth: number;
  showOnlyOpen: boolean;
  showBorder?: boolean;
  /** When set (e.g. in virtual list), constrains height so row border is visible */
  contentMaxHeight?: number;
}) {
  const handleClick = useCallback(() => {
    if (!job.isDisabled) {
      onSelect(job);
    }
  }, [job, onSelect]);
  const { color, label } =
    jobStatusConfig[job.status] || requisitionStatusConfig[job.status] || {};

  const locationText = renderJobLocation(job.locations);
  const hasDepartmentOrLocations = !!(job?.department && job?.locations);

  const jobContent = (
    <Box
      cursor={job.isDisabled ? 'not-allowed' : 'pointer'}
      bg={isSelected ? 'rgba(248, 249, 252, 0.5)' : 'white'}
      _hover={job.isDisabled ? {} : { bg: 'secondary-25' }}
      onClick={handleClick}
      opacity={job.isDisabled ? 0.5 : 1}
      gap="10px"
      p="12px"
      borderBottom={showBorder ? '1px solid' : undefined}
      borderColor={showBorder ? 'border-color' : undefined}
      width={`${menuWidth}px`}
      overflowX="hidden"
      {...(contentMaxHeight !== undefined && {
        maxHeight: contentMaxHeight,
        overflow: 'hidden',
      })}
    >
      <Flex
        alignItems="center"
        gap="6px"
        w={`${menuWidth - 10}px`}
        overflowX="hidden"
      >
        <Flex flexDirection="column" flex="1" minW="0" gap="2px">
          <Flex alignItems="center" gap="6px">
            <KText
              color="secondary-900"
              size="sm-medium"
              isTruncated
              enableTooltip
              maxW={
                showOnlyOpen
                  ? '100%'
                  : job.status === 'pending_approval'
                    ? '45%'
                    : '60%'
              }
              tooltipText={job.name}
            >
              {job.name}
            </KText>
            {!showOnlyOpen && (
              <KTag
                variant="secondary"
                size="sm"
                bg="white"
                border="1px solid"
                borderColor="border-color"
                value={
                  <Flex alignItems="center" gap="6px">
                    <Circle size="2" bg={color} />
                    <KText isTruncated>{label}</KText>
                  </Flex>
                }
              />
            )}
          </Flex>
          <Flex maxW="100%" overflowX="hidden" alignItems="center">
            {!hasDepartmentOrLocations ? (
              <KText
                color="secondary-500"
                size="sm-regular"
                maxW="100%"
                isTruncated
              >
                -
              </KText>
            ) : (
              <Flex
                as="span"
                alignItems="center"
                gap="4px"
                w={`${menuWidth - 50}px`}
                overflowX="hidden"
              >
                <KText
                  display="inline"
                  color="secondary-500"
                  size="sm-regular"
                  maxW="50%"
                  isTruncated
                  enableTooltip
                >
                  {job?.department ? job?.department : '-'}
                </KText>
                {job?.locations && job.department && (
                  <KText
                    display="inline"
                    color="secondary-500"
                    size="sm-regular"
                  >
                    ·
                  </KText>
                )}
                <KText
                  display="inline"
                  color="secondary-500"
                  size="sm-regular"
                  maxW="50%"
                >
                  {locationText}
                </KText>
              </Flex>
            )}
          </Flex>
        </Flex>
        {isSelected && (
          <Icon
            as={CheckCircle}
            h="18px"
            w="18px"
            color="primary-500"
            flexShrink={0}
            mr="30px"
          />
        )}
      </Flex>
    </Box>
  );

  // Wrap with tooltip if disabled and has a reason
  if (job.isDisabled && job.disableReason) {
    return (
      <Tooltip label={job.disableReason} hasArrow>
        {jobContent}
      </Tooltip>
    );
  }

  return jobContent;
});

type JobListRowData = {
  allOptions: JobListOption[];
  selectedSet: Set<string | number>;
  onSelect: (_option: JobListOption) => void;
  menuWidth: number;
  showOnlyOpen: boolean;
};

const BORDER_STYLE = '1px solid var(--kula-colors-border-color)';

const JobListRow = memo(function JobListRow({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: JobListRowData;
}) {
  const { allOptions, selectedSet, onSelect, menuWidth, showOnlyOpen } = data;
  const job = allOptions[index];
  if (!job) return null;
  return (
    <Box
      style={{
        ...style,
        borderBottom: BORDER_STYLE,
        boxSizing: 'border-box',
      }}
    >
      <JobItem
        job={job}
        onSelect={onSelect}
        isSelected={selectedSet.has(job.id)}
        menuWidth={menuWidth}
        showOnlyOpen={showOnlyOpen}
        showBorder={false}
        contentMaxHeight={getJobItemContentHeight(showOnlyOpen)}
      />
    </Box>
  );
});

// Job list component with virtualization for large lists
const LIST_HEIGHT = 280;

export const JobList = ({
  allOptions,
  onSelect,
  selected = [],
  menuWidth,
  showOnlyOpen,
  emptyStateText = 'No jobs found',
}: JobListProps) => {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const itemData = useMemo<JobListRowData>(
    () => ({
      allOptions,
      selectedSet,
      onSelect,
      menuWidth,
      showOnlyOpen,
    }),
    [allOptions, selectedSet, onSelect, menuWidth, showOnlyOpen]
  );

  const useVirtualList =
    allOptions.length > VIRTUAL_LIST_THRESHOLD && menuWidth > 0;

  if (allOptions.length === 0) {
    return (
      <Flex flexDir="column" bg="white" borderRadius="md" minW="340px">
        <Box p="4" textAlign="center">
          <Text color="gray.500">{emptyStateText}</Text>
        </Box>
      </Flex>
    );
  }

  if (useVirtualList) {
    return (
      <Flex flexDir="column" bg="white" borderRadius="md" minW="340px">
        <List
          height={Math.min(
            allOptions.length * getJobItemHeight(showOnlyOpen),
            LIST_HEIGHT
          )}
          width={menuWidth}
          itemCount={allOptions.length}
          itemSize={getJobItemHeight(showOnlyOpen)}
          itemData={itemData}
          overscanCount={5}
        >
          {JobListRow}
        </List>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" bg="white" borderRadius="md" minW="340px">
      {allOptions.map((job) => (
        <JobItem
          key={job.id}
          job={job}
          onSelect={onSelect}
          isSelected={selectedSet.has(job.id)}
          menuWidth={menuWidth}
          showOnlyOpen={showOnlyOpen}
        />
      ))}
    </Flex>
  );
};

// Helper functions for JobListMenu - memoized and optimized
const mapOptionsToJobListFormat = (options: JobOption[]): JobListOption[] => {
  return options.map((option) => ({
    id: option.value,
    name: option.label,
    status: option.status,
    department:
      typeof option.department === 'object' && option.department?.name
        ? option.department.name
        : (option.department as string),
    locations: option.locations,
    category: option.category,
    isDisabled: option.isDisabled,
    disableTag: option.disableTag,
    disableReason: option.disableReason,
  }));
};

const filterJobsBySearchTerm = (
  jobs: JobListOption[],
  searchTerm: string
): JobListOption[] => {
  if (!searchTerm) return jobs;

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  return jobs.filter((job) => {
    const name = job.name?.toLowerCase() || '';
    const department = job.department?.toLowerCase() || '';
    const locations = Array.isArray(job.locations)
      ? job.locations.join(' ').toLowerCase()
      : job.locations?.toLowerCase() || '';

    return (
      name.includes(normalizedSearchTerm) ||
      department.includes(normalizedSearchTerm) ||
      locations.includes(normalizedSearchTerm)
    );
  });
};

const filterPublishedJobs = (jobs: JobListOption[]): JobListOption[] => {
  return jobs.filter((job) => job.status?.toLowerCase() === 'published');
};

// Custom hook to get menu width (exported for external use)
// eslint-disable-next-line react-refresh/only-export-components -- hook shared from same module
export const useMenuWidth = () => {
  const [menuWidth, setMenuWidth] = useState<number>(0);

  const updateMenuWidth = useCallback((width: number) => {
    setMenuWidth(width);
  }, []);

  return { menuWidth, updateMenuWidth };
};

// Memoized toggle state hook
const useToggleState = () => {
  const [showOnlyOpen, setShowOnlyOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('jobList-showOnlyOpen');
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const updateShowOnlyOpen = useCallback((value: boolean) => {
    setShowOnlyOpen(value);
    try {
      localStorage.setItem('jobList-showOnlyOpen', JSON.stringify(value));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return [showOnlyOpen, updateShowOnlyOpen] as const;
};

export const JobListMenu = (props: JobListMenuProps) => {
  const { selectProps } = props;
  const jobOptions = selectProps.options;
  /* eslint-disable */
  const selectedValues = selectProps?.value || [];
  const onChange = selectProps.onChange as (options: JobOption[]) => void;
  const isMulti = !!selectProps.isMulti;
  const inputValue = (selectProps.inputValue as string) || '';
  const isLoading = selectProps.isLoading;
  const { extraProps = {}, emptyStateText = 'No jobs found' } =
    selectProps as any;
  const excludeStatuses =
    extraProps?.excludeStatuses || (selectProps as any).excludeStatuses || [];
  const showToggle =
    extraProps?.showToggle !== undefined
      ? extraProps.showToggle
      : (selectProps as any).showToggle !== undefined
        ? (selectProps as any).showToggle
        : true;
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number>(300);

  const [showOnlyOpen, updateShowOnlyOpen] = useToggleState();

  // Set menu width once when menu is mounted (width is from select control, not content)
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (el?.offsetWidth) {
      setMenuWidth(el.offsetWidth);
    }
  }, []);

  // Memoized computations
  const formattedOptions = useMemo(
    () => mapOptionsToJobListFormat(jobOptions),
    [jobOptions]
  );

  // Check if all jobs are already published
  const allJobsArePublished = useMemo(() => {
    return (
      formattedOptions.length > 0 &&
      formattedOptions.every((job) => job.status?.toLowerCase() === 'published')
    );
  }, [formattedOptions]);

  const filteredOptions = useMemo(() => {
    let filtered = formattedOptions;

    // Filter out excluded statuses if provided
    if (excludeStatuses.length > 0) {
      filtered = filtered.filter(
        (job) =>
          !excludeStatuses.some(
            (status) => job.status?.toLowerCase() === status.toLowerCase()
          )
      );
    }

    if (inputValue.trim()) {
      filtered = filterJobsBySearchTerm(filtered, inputValue);
    }

    if (showOnlyOpen) {
      filtered = filterPublishedJobs(filtered);
    }

    return filtered;
  }, [formattedOptions, inputValue, showOnlyOpen, excludeStatuses]);

  const selectedIds = useMemo(() => {
    if (isMulti) {
      return Array.isArray(selectedValues)
        ? selectedValues.map((val) => val.value as string | number)
        : [];
    } else {
      return selectedValues &&
        typeof selectedValues === 'object' &&
        'value' in selectedValues
        ? [selectedValues.value as string | number]
        : [];
    }
  }, [selectedValues, isMulti]);

  const handleSelect = useCallback(
    (selectedJob: JobListOption) => {
      // Prevent selecting disabled options
      if (selectedJob.isDisabled) return;

      let newSelectedOptions;

      const jobOption = jobOptions.find((opt) => opt.value === selectedJob.id);
      if (!jobOption) return;

      if (isMulti) {
        const isSelected = selectedValues?.some(
          (val) => val.value === selectedJob.id
        );

        if (isSelected) {
          newSelectedOptions = selectedValues.filter(
            (val) => val.value !== selectedJob.id
          );
        } else {
          newSelectedOptions = [...selectedValues, jobOption];
        }
      } else {
        newSelectedOptions = jobOption;
      }

      onChange(newSelectedOptions);

      // Clear the input value when making a selection
      if (selectProps.onInputChange) {
        selectProps.onInputChange('', { action: 'set-value' });
      }

      selectProps.onMenuClose?.();
    },
    [jobOptions, selectedValues, isMulti, onChange, selectProps]
  );

  const handleToggleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateShowOnlyOpen(!showOnlyOpen);

      setTimeout(() => {
        selectProps.onMenuOpen?.();
      }, 0);
    },
    [showOnlyOpen, updateShowOnlyOpen, selectProps]
  );

  const handleSwitchMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <components.Menu {...props}>
      <Box
        ref={menuRef}
        maxH="330px"
        maxW="100%"
        overflowX="hidden"
        position="relative"
      >
        {!allJobsArePublished && showToggle && (
          <Flex
            position="sticky"
            top="0"
            zIndex="1"
            alignItems="center"
            bg="secondary-25"
            justifyContent="space-between"
            p="12px"
            w="100%"
            borderBottom="1px solid"
            borderColor="border-color"
            maxW="100%"
            overflowX="hidden"
            borderTopRadius="4px"
          >
            <KText size="sm-medium" maxW="100%" isTruncated>
              Show open jobs only
            </KText>
            <Box
              onClick={handleToggleClick}
              onMouseDown={handleSwitchMouseDown}
            >
              <KSwitch
                className={`ats-switch-toogle ${
                  showOnlyOpen ? 'active' : 'unactive'
                }`}
                colorScheme="ats-primary"
                isChecked={showOnlyOpen}
                onChange={() => {}}
              />
            </Box>
          </Flex>
        )}
        <Box maxH="280px" overflowY="auto" overflowX="hidden">
          {isLoading ? (
            <Center p="4" h="280px">
              <Spinner size="sm" color="primary-800" />
            </Center>
          ) : (
            <JobList
              emptyStateText={emptyStateText}
              allOptions={filteredOptions}
              onSelect={handleSelect}
              selected={selectedIds}
              isMulti={isMulti}
              menuWidth={menuWidth}
              showOnlyOpen={showOnlyOpen || allJobsArePublished}
            />
          )}
        </Box>
      </Box>
    </components.Menu>
  );
};
